// ============================================================================
//  dAdmin  ›  dAttendance  ›  Work Pattern & Reminders   (combined page)
//
//  One table, every per-employee control in the same row: the work pattern
//  (dropdown + adhoc day picker, saved as a batch) AND the reminder controls
//  (sheet status + Send R1 / R2). It merges the two month endpoints by emp_id:
//    /api/dattendance/config/month     → pattern, adhoc, working days, calendar
//    /api/dattendance/reminders/month  → sheet status, reminder sent times
//  and reuses the day picker, modals and helpers exported by the two original
//  pages so there is a single source of truth for each piece of behaviour.
// ============================================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { FaSearch, FaUmbrellaBeach, FaFileExcel, FaExternalLinkAlt, FaHistory } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { exportTicketsExcel } from "./utils/ticketExport";
import logo_eagle from "./assets/img/logo_eagle.png";
import {
    PATTERN_OPTIONS, PATTERN_LABEL, normPattern, baseTypeFor, SELECT_STYLES, DayPicker,
    STATUS_PILL, SUBMITTED, fmtSentAt, Modal, MailPreview, BulkPeek,
} from "./dAttendance_shared";
import "./dSpr.css";
import "./dAttendance_WorkPattern.css";
import "./dAttendance_Reminders.css";

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

export default function DAttendanceManage({ navSize }) {
    const navigate = useNavigate();
    const now = new Date();
    const [years, setYears] = useState([]);
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    const [data, setData] = useState(null);          // { employees:[merged], holidays, holiday_warning }
    const [draft, setDraft] = useState({});           // emp_id -> { pattern, adhoc_days }
    const [openEmps, setOpenEmps] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState(null);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [deptFilter, setDeptFilter] = useState(null);
    const [locFilter, setLocFilter] = useState(null);

    const [holOpen, setHolOpen] = useState(false);
    const holRef = useRef(null);

    const [single, setSingle] = useState(null);       // reminder single-send preview
    const [bulk, setBulk] = useState(null);           // reminder bulk modal
    const [bulkSearch, setBulkSearch] = useState("");
    const [history, setHistory] = useState(null);     // reminder send-history modal

    const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2600); };
    const toggleOpen = (empId) => setOpenEmps((prev) => {
        const next = new Set(prev);
        next.has(empId) ? next.delete(empId) : next.add(empId);
        return next;
    });

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/dattendance/config/years");
                const json = await res.json();
                if (json.success) { setYears(json.years); setYear((y) => (json.years.includes(y) ? y : json.years[0])); }
            } catch { /* month load surfaces errors */ }
        })();
    }, []);

    // Load BOTH month endpoints and merge them per employee.
    const load = useCallback(async (y, m, { silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError("");
        try {
            const [cfgRes, remRes] = await Promise.all([
                apiFetch(`/api/dattendance/config/month?year=${y}&month=${m}`),
                apiFetch(`/api/dattendance/reminders/month?year=${y}&month=${m}`),
            ]);
            const cfg = await cfgRes.json();
            const rem = await remRes.json().catch(() => ({}));
            if (!cfgRes.ok) throw new Error(cfg.error || "Failed to load");
            const remByEmp = new Map((rem.employees || []).map((r) => [r.emp_id, r]));
            const employees = (cfg.employees || []).map((e) => {
                const r = remByEmp.get(e.emp_id);
                return {
                    ...e,
                    pattern: normPattern(e.pattern),
                    sheet_status: r?.sheet_status ?? null,
                    emp_mail_id: r?.emp_mail_id ?? null,
                    reminders: r?.reminders ?? [
                        { reminder_no: 1, sent_time: null }, { reminder_no: 2, sent_time: null },
                    ],
                    app_enabled: !!r,     // present in the reminders list => app_dAttendance = 1
                };
            });
            setData({ employees, holidays: cfg.holidays || [], holiday_warning: cfg.holiday_warning });
            setDraft({});
            if (!silent) setOpenEmps(new Set());
        } catch (err) {
            if (!silent) setData(null);
            setError(err.message);
        } finally { if (!silent) setLoading(false); }
    }, []);

    useEffect(() => { load(year, month); }, [year, month, load]);

    // After a reminder send we only need to refresh the reminder/sheet fields —
    // merge them into the existing rows WITHOUT touching pattern data or the
    // unsaved work-pattern draft, and without a page reload.
    const refreshReminders = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/dattendance/reminders/month?year=${year}&month=${month}`);
            const json = await res.json();
            if (!res.ok) return;
            const remByEmp = new Map((json.employees || []).map((r) => [r.emp_id, r]));
            setData((prev) => prev ? {
                ...prev,
                employees: prev.employees.map((e) => {
                    const r = remByEmp.get(e.emp_id);
                    return r ? {
                        ...e,
                        sheet_status: r.sheet_status ?? e.sheet_status,
                        emp_mail_id: r.emp_mail_id ?? e.emp_mail_id,
                        reminders: r.reminders ?? e.reminders,
                        app_enabled: true,
                    } : e;
                }),
            } : prev);
        } catch { /* leave rows as-is on a refresh failure */ }
    }, [year, month]);

    useEffect(() => {
        if (!holOpen) return undefined;
        const onDown = (e) => { if (holRef.current && !holRef.current.contains(e.target)) setHolOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [holOpen]);

    // ── work-pattern draft ────────────────────────────────────────────────
    const effective = useCallback((emp) => draft[emp.emp_id] || {
        pattern: emp.pattern, adhoc_days: emp.adhoc_days,
    }, [draft]);

    const dirtyIds = useMemo(() => {
        if (!data) return [];
        return Object.keys(draft).filter((id) => {
            const server = data.employees.find((e) => e.emp_id === id);
            if (!server) return false;
            const d = draft[id];
            return d.pattern !== server.pattern ||
                   [...d.adhoc_days].sort().join() !== [...server.adhoc_days].sort().join();
        });
    }, [draft, data]);

    const setPattern = (emp, pattern) => {
        const cur = effective(emp);
        const keptAdhoc = cur.adhoc_days.filter((d) => {
            const day = emp.days.find((x) => x.date === d);
            return day && baseTypeFor(day, pattern) !== "WORK";
        });
        setDraft((p) => ({ ...p, [emp.emp_id]: { pattern, adhoc_days: keptAdhoc } }));
    };

    const toggleAdhoc = (emp, date) => {
        const cur = effective(emp);
        const has = cur.adhoc_days.includes(date);
        setDraft((p) => ({
            ...p,
            [emp.emp_id]: {
                pattern: cur.pattern,
                adhoc_days: has ? cur.adhoc_days.filter((d) => d !== date) : [...cur.adhoc_days, date].sort(),
            },
        }));
    };

    const savePatterns = async () => {
        if (!dirtyIds.length) return;
        setBusy(true);
        try {
            const changes = dirtyIds.map((id) => ({
                emp_id: id, pattern: draft[id].pattern, adhoc_days: draft[id].adhoc_days,
            }));
            const res = await apiFetch("/api/dattendance/config/save-patterns", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ year, month, changes }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || "Save failed");
            flash(`${json.saved} change${json.saved === 1 ? "" : "s"} saved`);
            await load(year, month, { silent: true });
        } catch (err) { flash(err.message); }
        finally { setBusy(false); }
    };

    // ── reminders ─────────────────────────────────────────────────────────
    const shownStatus = (row) => {
        if (row.sheet_status && row.sheet_status !== "draft") return row.sheet_status;
        const isPast = year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);
        if (isPast) return "completed";
        return row.sheet_status || "not_started";
    };

    const openSingle = async (emp_id, which) => {
        try {
            const res = await apiFetch(
                `/api/dattendance/reminders/preview?emp_id=${encodeURIComponent(emp_id)}&year=${year}&month=${month}&which=${which}`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || "Preview failed");
            setSingle({ emp_id, which, ...json });
        } catch (err) { flash(err.message); }
    };

    const send = async (which, emp_ids) => {
        setBusy(true);
        try {
            const res = await apiFetch("/api/dattendance/reminders/send", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ year, month, which, emp_ids }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || "Send failed");
            flash(`Reminder ${which} sent to ${json.sent.length}` + (json.skipped.length ? ` · ${json.skipped.length} skipped` : ""));
            setSingle(null); setBulk(null);
            await refreshReminders();   // update reminder cells in place; keep pattern draft intact
        } catch (err) { flash(err.message); }
        finally { setBusy(false); }
    };

    const openBulk = (which) => {
        const hasSent = (r, n) => !!r.reminders.find((x) => x.reminder_no === n)?.sent_time;
        let candidates = (data?.employees || [])
            .filter((r) => r.app_enabled && r.sheet_status !== "approved" && !hasSent(r, which));
        if (which === 2) candidates = candidates.filter((r) => hasSent(r, 1));
        setBulkSearch("");
        setBulk({
            which, candidates,
            selected: candidates.filter((r) => !SUBMITTED.includes(r.sheet_status)).map((r) => r.emp_id),
            peek: null,
        });
    };

    const fetchHistory = async (f) => {
        setHistory({ ...f, rows: [], loading: true });
        const p = new URLSearchParams();
        if (f.fEmp) p.set("emp_id", f.fEmp);
        if (f.fYear) p.set("year", f.fYear);
        if (f.fMonth) p.set("month", f.fMonth);
        try {
            const res = await apiFetch(`/api/dattendance/reminders/history?${p.toString()}`);
            const json = await res.json();
            setHistory({ ...f, rows: Array.isArray(json.rows) ? json.rows : [], loading: false });
        } catch { setHistory({ ...f, rows: [], loading: false }); }
    };
    const openHistory = () => fetchHistory({ fEmp: "", fYear: String(year), fMonth: "" });
    const setHistFilter = (patch) => fetchHistory({ ...history, ...patch });

    // ── filtering + export ────────────────────────────────────────────────
    const empList = data?.employees || [];
    const deptOptions = [...new Set(empList.map((e) => e.department_name).filter(Boolean))].sort().map((d) => ({ value: d, label: d }));
    const locOptions = [...new Set(empList.map((e) => e.emp_location).filter(Boolean))].sort().map((l) => ({ value: l, label: l }));
    const q = search.trim().toLowerCase();
    const visible = empList.filter((e) =>
        (!deptFilter || (e.department_name || "") === deptFilter.value) &&
        (!locFilter || (e.emp_location || "") === locFilter.value) &&
        (!q || `${e.emp_name} ${e.emp_id} ${e.job_name || ""}`.toLowerCase().includes(q))
    );

    const exportExcel = () => {
        const sentAt = (r, n) => {
            const rem = r.reminders.find((x) => x.reminder_no === n);
            return rem && rem.sent_time ? new Date(rem.sent_time).toLocaleString("en-IN") : "Not sent";
        };
        const columns = [
            { key: "emp_id", label: "Emp ID", width: 14 },
            { key: "emp", label: "Employee", width: 26 },
            { key: "job", label: "Job position", width: 24 },
            { key: "location", label: "Location", width: 16 },
            { key: "dept", label: "Department", width: 26 },
            { key: "working", label: "Working days", width: 13 },
            { key: "pattern", label: "Work pattern", width: 16 },
            { key: "adhoc", label: "Adhoc days", width: 12 },
            { key: "status", label: "Sheet status", width: 16 },
            { key: "r1", label: "R1 sent", width: 22 },
            { key: "r2", label: "R2 sent", width: 22 },
        ];
        const rows = visible.map((e) => {
            const cfg = effective(e);
            const [, label] = STATUS_PILL[shownStatus(e)] || ["", shownStatus(e)];
            return {
                emp_id: e.emp_id, emp: e.emp_name, job: e.job_name || "-",
                location: e.emp_location || "-", dept: e.department_name || "-",
                working: e.joined_by_month_end ? e.totals.working_days : "-",
                pattern: PATTERN_LABEL[cfg.pattern] || cfg.pattern,
                adhoc: cfg.adhoc_days.length, status: label,
                r1: sentAt(e, 1), r2: sentAt(e, 2),
            };
        });
        exportTicketsExcel({
            moduleName: "dAttendance", sheetTitle: `Work Pattern & Reminders ${MONTHS[month - 1]} ${year}`,
            columns, rows, filterLabel: "",
            filename: `attendance_manage_${year}-${String(month).padStart(2, "0")}.xlsx`,
        });
    };

    return (
        <div className="dspr-container">
          <div className={`dspr-dashboard ${navSize || ""}`}>
            <div className="dspr-header-wrapper">
              <h2>
                <img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} />
                Attendance Manager
              </h2>
              <p className="dspr-subtitle">
                Everything for the month in one row per employee - set the working week and adhoc
                days, see the sheet status, and send reminder 1 or 2.
              </p>
            </div>
            <div className="dspr-dashboard-content datt-page">
              <div className="datt-monthbar">
                <div className="datt-months">
                    {MONTHS.map((name, i) => (
                        <button key={name} type="button"
                            className={`datt-month${month === i + 1 ? " is-active" : ""}`}
                            onClick={() => setMonth(i + 1)}>{name.slice(0, 3)}</button>
                    ))}
                </div>
                <div className="datt-year">
                    <span>Year</span>
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
              </div>

              {error && <div className="datt-notice is-red">{error}</div>}

              {data && (
                <div className="datt-card datt-toolcard">
                    <div className="datt-toolcard-left">
                        <span className="datt-month-label">{MONTHS[month - 1]} {year}</span>
                        <div className="datt-filterbar">
                            <div className="datt-search-wrap">
                                <FaSearch className="datt-search-icon" />
                                <input type="text" className="datt-search" placeholder="Search name or emp ID…"
                                    value={search} onChange={(e) => setSearch(e.target.value)} />
                                {search && (
                                    <button type="button" className="datt-search-clear"
                                        onClick={() => setSearch("")} aria-label="Clear search">×</button>
                                )}
                            </div>
                            <Select className="datt-select-wrap" options={deptOptions} value={deptFilter}
                                onChange={setDeptFilter} isClearable placeholder="All departments"
                                styles={SELECT_STYLES} menuPortalTarget={document.body} />
                            <Select className="datt-select-wrap" options={locOptions} value={locFilter}
                                onChange={setLocFilter} isClearable placeholder="All locations"
                                styles={SELECT_STYLES} menuPortalTarget={document.body} />
                            <button type="button" className="datt-btn is-export" onClick={exportExcel}
                                disabled={visible.length === 0} title="Export this month to Excel">
                                <FaFileExcel style={{ marginRight: 6 }} /> Export Excel
                            </button>
                        </div>
                    </div>
                    <span className="datt-bulk-actions">
                        <button type="button" className="datt-hist-icon" title="Reminder send history" onClick={openHistory}>
                            <FaHistory />
                        </button>
                        <button type="button" className="datt-btn is-ghost is-sm" onClick={() => openBulk(1)}>Send R1 to all</button>
                        <button type="button" className="datt-btn is-ghost is-sm" onClick={() => openBulk(2)}>Send R2 to all</button>
                        <div className={`datt-holiday-icon-wrap${holOpen ? " is-open" : ""}`} ref={holRef}>
                            <button type="button" className="datt-holiday-icon" aria-label="Holidays this month"
                                aria-expanded={holOpen} onClick={() => setHolOpen((o) => !o)}>
                                <FaUmbrellaBeach />
                                {data.holidays.length > 0 && <span className="datt-holiday-count">{data.holidays.length}</span>}
                            </button>
                            <div className="datt-holiday-pop">
                                <div className="datt-holiday-pop-title">
                                    <span>Holidays this month</span>
                                    <span className="datt-holiday-pop-link" role="link" tabIndex={0}
                                        title="Open the Holiday page" onClick={() => navigate("/Holiday")}
                                        onKeyDown={(e) => { if (e.key === "Enter") navigate("/Holiday"); }}>
                                        <FaExternalLinkAlt />
                                    </span>
                                </div>
                                {data.holidays.length > 0 ? (
                                    <div className="datt-holiday-poplist">
                                        {data.holidays.map((h) => (
                                            <div key={h.holiday_id} className="datt-holiday-popitem">
                                                <strong>{h.holiday_name}</strong>
                                                <em>
                                                    {h.holiday_date}
                                                    {h.holiday_end && h.holiday_end !== h.holiday_date ? ` → ${h.holiday_end}` : ""}
                                                    {" · "}
                                                    {h.holiday_for === "general" ? "All Staffs" : `${h.holiday_for}: ${h.holiday_value}`}
                                                </em>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="datt-holiday-popempty">{data.holiday_warning || "No holidays configured this month."}</div>
                                )}
                            </div>
                        </div>
                    </span>
                </div>
              )}

              {loading && <div className="datt-card datt-loading">Loading {MONTHS[month - 1]} {year}…</div>}

              {!loading && data && (
                <div className="datt-card">
                    <div className="datt-table-wrap">
                        <table className="datt-table">
                            <thead>
                                <tr>
                                    <th>Emp ID</th><th>Employee name</th><th>Job position</th>
                                    <th>Location</th><th>Department</th><th>Working days</th>
                                    <th>Work pattern</th><th /><th>Sheet status</th>
                                    <th className="datt-center">Reminder 1</th>
                                    <th className="datt-center">Reminder 2</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.length === 0 && (
                                    <tr><td colSpan={11} className="datt-empty">No employees match these filters.</td></tr>
                                )}
                                {visible.map((emp) => {
                                    const cfg = effective(emp);
                                    const isDirty = dirtyIds.includes(emp.emp_id);
                                    const isOpen = openEmps.has(emp.emp_id);
                                    const [tone, label] = STATUS_PILL[shownStatus(emp)] || ["grey", shownStatus(emp)];
                                    const r1Sent = !!emp.reminders.find((x) => x.reminder_no === 1)?.sent_time;
                                    const approved = emp.sheet_status === "approved";
                                    return (
                                        <React.Fragment key={emp.emp_id}>
                                            <tr className={isOpen ? "is-open" : isDirty ? "is-dirty" : ""}>
                                                <td className="datt-mono">{emp.emp_id}</td>
                                                <td className="datt-strong">
                                                    {emp.emp_name}
                                                    {!emp.joined_by_month_end && <span className="datt-pill is-grey">Not joined</span>}
                                                    {isDirty && <span className="datt-pill is-amber">Unsaved</span>}
                                                </td>
                                                <td className="datt-muted">{emp.job_name || "-"}</td>
                                                <td className="datt-muted">{emp.emp_location || "-"}</td>
                                                <td className="datt-muted datt-small">{emp.department_name || "-"}</td>
                                                <td>{emp.joined_by_month_end ? emp.totals.working_days : "-"}</td>
                                                <td>
                                                    <select value={cfg.pattern} disabled={!emp.joined_by_month_end}
                                                        onChange={(e) => setPattern(emp, e.target.value)}>
                                                        {PATTERN_OPTIONS.map((p) => (
                                                            <option key={p} value={p}>{PATTERN_LABEL[p]}</option>
                                                        ))}
                                                    </select>
                                                    {cfg.adhoc_days.length > 0 && (
                                                        <span className="datt-pill is-orange">
                                                            {cfg.adhoc_days.length} adhoc day{cfg.adhoc_days.length > 1 ? "s" : ""}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="datt-right">
                                                    <button type="button" className="datt-btn is-ghost is-sm"
                                                        disabled={!emp.joined_by_month_end}
                                                        onClick={() => toggleOpen(emp.emp_id)}>
                                                        {isOpen ? "Hide days" : "Adhoc support"}
                                                    </button>
                                                </td>
                                                <td><span className={`datt-pill is-${tone}`} style={{ marginLeft: 0 }}>{label}</span></td>
                                                {[1, 2].map((w) => {
                                                    const rem = emp.reminders.find((x) => x.reminder_no === w);
                                                    if (!emp.app_enabled) {
                                                        return <td key={w} className="datt-center datt-muted">-</td>;
                                                    }
                                                    if (rem.sent_time) {
                                                        return (
                                                            <td key={w} className="datt-center">
                                                                <span className="datt-sent-at"
                                                                    title={`Reminder ${w} sent ${new Date(rem.sent_time).toLocaleString("en-IN")}`}>
                                                                    <span className="datt-sent-tick">✓ {fmtSentAt(rem.sent_time)}</span>
                                                                </span>
                                                            </td>
                                                        );
                                                    }
                                                    const needsR1 = w === 2 && !r1Sent;
                                                    const reason = approved
                                                        ? "Attendance already approved - no reminder needed"
                                                        : needsR1 ? "Send Reminder 1 first" : "";
                                                    return (
                                                        <td key={w} className="datt-center">
                                                            <span title={reason || undefined} style={{ display: "inline-block" }}>
                                                                <button type="button" className="datt-btn is-sm is-primary"
                                                                    disabled={!!reason}
                                                                    onClick={() => !reason && openSingle(emp.emp_id, w)}>
                                                                    Send R{w}
                                                                </button>
                                                            </span>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                            {isOpen && (
                                                <tr>
                                                    <td colSpan={11} className="datt-expand-cell">
                                                        <DayPicker emp={emp} cfg={cfg} onToggle={(d) => toggleAdhoc(emp, d)} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
              )}

              {dirtyIds.length > 0 && (
                <>
                    <div className="datt-savebar-spacer" />
                    <div className="datt-savebar">
                        <span>
                            {dirtyIds.length} unsaved work-pattern change{dirtyIds.length === 1 ? "" : "s"}.
                            Nothing has been written to the database yet.
                        </span>
                        <span className="datt-savebar-actions">
                            <button type="button" className="datt-btn is-quiet is-sm" disabled={busy}
                                onClick={() => setDraft({})}>Discard</button>
                            <button type="button" className="datt-btn is-primary is-sm" disabled={busy}
                                onClick={savePatterns}>{busy ? "Saving…" : "Save changes"}</button>
                        </span>
                    </div>
                </>
              )}

              {/* single reminder send */}
              {single && (
                <Modal title={`Reminder ${single.which} - email preview`} onClose={() => setSingle(null)}
                    foot={<>
                        <button type="button" className="datt-btn is-ghost" onClick={() => setSingle(null)}>Cancel</button>
                        <button type="button" className="datt-btn is-primary" disabled={busy}
                            onClick={() => send(single.which, [single.emp_id])}>Send email</button>
                    </>}>
                    <MailPreview mail={single.mail} employee={single.employee} />
                </Modal>
              )}

              {/* bulk reminder send */}
              {bulk && !bulk.peek && (
                <Modal wide title={`Send reminder ${bulk.which} - ${MONTHS[month - 1]} ${year}`}
                    onClose={() => setBulk(null)}
                    foot={<>
                        <button type="button" className="datt-btn is-ghost" onClick={() => setBulk(null)}>Cancel</button>
                        <button type="button" className="datt-btn is-primary"
                            disabled={busy || bulk.selected.length === 0}
                            onClick={() => send(bulk.which, bulk.selected)}>
                            Send to {bulk.selected.length} employee{bulk.selected.length === 1 ? "" : "s"}
                        </button>
                    </>}>
                    {bulk.candidates.length === 0 ? (
                        <p className="datt-modal-note">No employees are eligible for this reminder.</p>
                    ) : (
                        <>
                            <div className="datt-bulk-head">
                                <div className="datt-search-wrap" style={{ marginBottom: 0, maxWidth: 300, flex: "0 0 auto" }}>
                                    <FaSearch className="datt-search-icon" />
                                    <input type="text" className="datt-search" placeholder="Search name or emp ID…"
                                        value={bulkSearch} onChange={(e) => setBulkSearch(e.target.value)} />
                                    {bulkSearch && (
                                        <button type="button" className="datt-search-clear"
                                            onClick={() => setBulkSearch("")} aria-label="Clear search">×</button>
                                    )}
                                </div>
                                <p className="datt-modal-note" style={{ margin: 0, flex: 1, maxWidth: "none", whiteSpace: "nowrap" }}>
                                    Employees who have already submitted are unticked by default. Check each mail before sending.
                                </p>
                                <button type="button" className="datt-btn is-ghost is-sm"
                                    onClick={() => setBulk((b) => ({
                                        ...b,
                                        selected: b.selected.length === b.candidates.length ? [] : b.candidates.map((c) => c.emp_id),
                                    }))}>
                                    {bulk.selected.length === bulk.candidates.length ? "Clear all" : "Select all"}
                                </button>
                            </div>
                            <div className="datt-bulk-list">
                                {(() => {
                                    const s = bulkSearch.trim().toLowerCase();
                                    const shown = bulk.candidates.filter((c) =>
                                        !s || `${c.emp_name} ${c.emp_id} ${c.emp_mail_id || ""}`.toLowerCase().includes(s));
                                    if (shown.length === 0) return <div className="datt-empty">No employees match this search.</div>;
                                    return shown.map((c) => {
                                        const on = bulk.selected.includes(c.emp_id);
                                        const [tone, label] = STATUS_PILL[shownStatus(c)] || ["grey", "-"];
                                        return (
                                            <div key={c.emp_id} className={`datt-bulk-row${on ? "" : " is-off"}`}>
                                                <input type="checkbox" checked={on}
                                                    onChange={() => setBulk((b) => ({
                                                        ...b,
                                                        selected: on ? b.selected.filter((x) => x !== c.emp_id) : [...b.selected, c.emp_id],
                                                    }))} />
                                                <div className="datt-bulk-who">
                                                    <span className="datt-bulk-name">{c.emp_name}</span>
                                                    <span className="datt-bulk-mail">{c.emp_id} · {c.emp_mail_id || "no email on file"}</span>
                                                </div>
                                                <span className={`datt-pill is-${tone}`}>{label}</span>
                                                <button type="button" className="datt-btn is-quiet is-sm"
                                                    onClick={() => setBulk((b) => ({ ...b, peek: c.emp_id }))}>Preview</button>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </>
                    )}
                </Modal>
              )}

              {bulk?.peek && (
                <BulkPeek empId={bulk.peek} which={bulk.which} year={year} month={month}
                    onBack={() => setBulk((b) => ({ ...b, peek: null }))} />
              )}

              {/* reminder send-history */}
              {history && (
                <Modal wide title="Reminder send history" onClose={() => setHistory(null)}
                    foot={<button type="button" className="datt-btn is-ghost" onClick={() => setHistory(null)}>Close</button>}>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                        <select value={history.fEmp} onChange={(e) => setHistFilter({ fEmp: e.target.value })}>
                            <option value="">All employees</option>
                            {empList.map((r) => <option key={r.emp_id} value={r.emp_id}>{r.emp_name} ({r.emp_id})</option>)}
                        </select>
                        <select value={history.fMonth} onChange={(e) => setHistFilter({ fMonth: e.target.value })}>
                            <option value="">Any month</option>
                            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                        <select value={history.fYear} onChange={(e) => setHistFilter({ fYear: e.target.value })}>
                            <option value="">Any year</option>
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    {history.loading ? (
                        <div className="datt-loading">Loading…</div>
                    ) : history.rows.length === 0 ? (
                        <div className="datt-empty">No reminder emails have been sent for this filter.</div>
                    ) : (
                        <div className="datt-table-wrap">
                            <table className="datt-table" style={{ minWidth: 0 }}>
                                <thead><tr><th>Sent</th><th>Employee</th><th>Reminder</th><th>By</th></tr></thead>
                                <tbody>
                                    {history.rows.map((h) => (
                                        <tr key={h.activity_id}>
                                            <td className="datt-nowrap">{new Date(h.created_time).toLocaleString("en-IN")}</td>
                                            <td className="datt-strong">{h.emp_name}<div className="datt-mono datt-muted">{h.emp_id}</div></td>
                                            <td>{h.detail}</td>
                                            <td className="datt-muted">{h.actor_name || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal>
              )}

              {toast && <div className="datt-toast">{toast}</div>}
            </div>
          </div>
        </div>
    );
}
